import React, { useState, useEffect, useRef, useCallback } from "react";

// import BackToServicesButton from "../components/BackToServicesButton";
import API_BASE_URL from "../api";
import { authFetch } from "../authFetch";
import { useAuth } from "../authContext";
import useSelectedRadio from "../hooks/useSelectedRadio";
import { resolveCountryFlag } from "../constants/countries";
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';

export default function LiveServicePage() {
  const { radioId, shortcode } = useSelectedRadio();
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [publicLink, setPublicLink] = useState(null);
  const [publicLinkLoading, setPublicLinkLoading] = useState(false);
  const [publicLinkError, setPublicLinkError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalMessage, setModalMessage] = useState(null);

  const intervalRef = useRef();
  const { isAuth, user } = useAuth();
  const isDemo = String(user?.email || "").toLowerCase() === "demo@radiomonitor.net";
  const { t } = useTranslation('common');
  const locale = useLocale();

  // Обновление данных без мигания (нет сброса loading)
  const fetchLive = useCallback(() => {
    if (!isAuth || !radioId) return;
    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    authFetch(
      `${API}/radios/${encodeURIComponent(radioId)}/metrics/live`
    )
      .then((r) => r.json())
      .then((json) => setData(json.data || []))
      .catch(() => setData([]));
    authFetch(
      `${API}/radios/${encodeURIComponent(radioId)}/metrics/live/count`
    )
      .then((r) => r.json())
      .then((json) => setCount(json.count || 0))
      .catch(() => setCount(0));
  }, [isAuth, radioId]);


  // Первое обновление: показываем loading
  useEffect(() => {
    if (!isAuth || !radioId) return;
    setLoading(true);
    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    authFetch(`${API}/radios/${encodeURIComponent(radioId)}/metrics/live`)
      .then((r) => r.json())
      .then((json) => setData(json.data || []))
      .catch(() => setData([]));
    authFetch(`${API}/radios/${encodeURIComponent(radioId)}/metrics/live/count`)
      .then((r) => r.json())
      .then((json) => setCount(json.count || 0))
      .catch(() => setCount(0))
      .finally(() => setLoading(false));
  }, [radioId, isAuth]);

  // Дальнейшие обновления только данных, без мигания и без setLoading
  useEffect(() => {
    if (!isAuth || !radioId) return;
    intervalRef.current = setInterval(fetchLive, 5000);
    return () => clearInterval(intervalRef.current);
  }, [radioId, isAuth, fetchLive]);


  const handleGetPublicLink = useCallback(() => {
    if (!isAuth || !shortcode) return;
    if (isDemo) {
      window.dispatchEvent(
        new CustomEvent("demo:block", { detail: { action: "get_public_counter" } })
      );
      return;
    }

    setPublicLinkLoading(true);
    setPublicLinkError(null);

    const API = (API_BASE_URL || "").replace(/\/+$/, "");

    authFetch(
      `${API}/radios/send-public-counter-link?channel=${encodeURIComponent(shortcode)}`,
      { method: "POST" }
    )
      .then((r) =>
        r.ok ? r.json() : r.json().then((j) => Promise.reject(j))
      )
      .then((json) => {
        // помечаем, что внешний лічильник увімкнено (щоб показати кнопку вимкнення)
        setPublicLink(true);
        setModalMessage(t('live.publicCounter.modalSent'));
      })
      .catch((err) => {
        const msg =
          (err && err.detail) ||
          t('live.publicCounter.errorGet');
        setPublicLinkError(msg);
      })
      .finally(() => {
        setPublicLinkLoading(false);
      });
  }, [isAuth, shortcode, t, isDemo]);

  const handleDisablePublicLink = useCallback(() => {
    if (!isAuth || !radioId) return;

    setPublicLinkLoading(true);
    setPublicLinkError(null);

    const API = (API_BASE_URL || "").replace(/\/+$/, "");

    authFetch(
      `${API}/radios/${encodeURIComponent(radioId)}/metrics/public-counter/disable`,
      { method: "POST" }
    )
      .then((r) =>
        r.ok ? r.json() : r.json().then((j) => Promise.reject(j))
      )
      .then(() => {
        setPublicLink(null);
        setModalMessage(t('live.publicCounter.modalDisabled'));
      })
      .catch((err) => {
        const msg =
          (err && err.detail) ||
          t('live.publicCounter.errorDisable');
        setPublicLinkError(msg);
      })
      .finally(() => {
        setPublicLinkLoading(false);
      });
  }, [isAuth, radioId, t]);

  useEffect(() => {
    if (!modalMessage) return;
    const timer = setTimeout(() => setModalMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [modalMessage]);

  useEffect(() => {
    if (!isAuth || !radioId) {
      setPublicLink(null);
      return;
    }

    const API = (API_BASE_URL || "").replace(/\/+$/, "");

    authFetch(
      `${API}/radios/${encodeURIComponent(radioId)}/metrics/public-counter/status`
    )
      .then((r) =>
        r.ok ? r.json() : r.json().then((j) => Promise.reject(j))
      )
      .then((json) => {
        setPublicLink(json.has_public_counter ? true : null);
        // при успешном запросе очищаем возможную старую ошибку
        setPublicLinkError(null);
      })
      .catch(() => {
        // тихо игнорируем ошибку статуса, кнопка просто не появится
      });
  }, [isAuth, radioId]);


  // ... код ниже не меняется

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        {/* BackToServicesButton removed */}
        <h2 style={{ marginBottom: 8 }}>
          <span role="img" aria-label="live">💡</span> Активні слухачі
        </h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          Немає підключеного каналу для користувача
        </div>
      </div>
    );
  }

  const now = Date.now();
  const retans = [], newbies = [], others = [];

  data.forEach((r) => {
    const st = r.start_time ? new Date(r.start_time).getTime() : 0;
    const lt = r.last_seen ? new Date(r.last_seen).getTime() : st;
    const dur = Math.floor((lt - st) / 1000);
    const flag = resolveCountryFlag(r);
    const base = { ...r, flag };
    if (dur < 30) {
      newbies.push({ ...base, start: st });
    } else if (dur >= 10800) {
      retans.push({ ...base, dur });
    } else {
      others.push({ ...base, dur });
    }
  });
  retans.sort((a, b) => b.dur - a.dur);
  others.sort((a, b) => b.dur - a.dur);
  if (others.length > 30) others.length = 30;

  return (
    <div style={widgetStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div style={{ flex: "1 1 260px" }}>
          {/* BackToServicesButton removed */}

          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {!publicLink && (
                <button
                  type="button"
                  onClick={handleGetPublicLink}
                  disabled={publicLinkLoading || !shortcode}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 6,
                    border: "none",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: publicLinkLoading ? "default" : "pointer",
                    background: "#00723a",
                    color: "#fff",
                    opacity: publicLinkLoading ? 0.6 : 1,
                  }}
                >
                  {publicLinkLoading
                    ? t('live.publicCounter.getLoading')
                    : t('live.publicCounter.get')}
                </button>
              )}

              {publicLink && (
                <button
                  type="button"
                  onClick={handleDisablePublicLink}
                  disabled={publicLinkLoading || !radioId}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 6,
                    border: "3px solid #00723a",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: publicLinkLoading ? "default" : "pointer",
                    background: "#fff",
                    color: "#00723a",
                    opacity: publicLinkLoading ? 0.6 : 1,
                  }}
                >
                  {publicLinkLoading
                    ? t('live.publicCounter.disableLoading')
                    : t('live.publicCounter.disable')}
                </button>
              )}
            </div>

            {publicLinkError && (
              <div style={{ marginTop: 8, color: "#e54848", fontSize: 13 }}>
                {publicLinkError}
              </div>
            )}
          </div>
        </div>

        <div style={counterBox}>
          <div style={{ fontSize: 64, fontWeight: 700, color: count ? "#f33" : "#bbb" }}>
            {fmtNumber(count, locale)}
          </div>
          <div style={{ fontWeight: 600, color: "#e54848", textAlign: "center" }}>
            {t('live.counter')}
          </div>
        </div>
      </div>

      {/* ТЕКСТОВОЕ ПОЛЕ */}
      <div
        style={{
          width: "100%",
          padding: "12px 14px",
          borderRadius: 8,
          background: "#fff7f7",
          color: "#e60000",
          fontSize: 34,
          fontWeight: 700,
          textAlign: "center",
          margin: "14px 0 16px 0",
          boxSizing: "border-box",
          border: "4px solid #ff3200",
          letterSpacing: 1
        }}
      >
        {t('live.banner')}
      </div>

      {loading ? (
        <div style={{ margin: "24px 0" }}>{t('common.loading')}</div>
      ) : (
        <>
          <div style={{ textAlign: "right", fontWeight: "bold", margin: "0 0 8px 0", fontSize: 20 }}>
            <span role="img" aria-label="listeners">👥</span> {t('live.total', { count, value: fmtNumber(count, locale) })}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 16 }}>
              <thead>
                <tr>
                  <th style={thTd}>🌐 {t('live.table.ip')}</th>
                  <th style={thTd}>⏱ {t('live.table.duration')}</th>
                  <th style={thTd}>🌍 {t('live.table.country')}</th>
                  <th style={thTd}>
                    <span role="img" aria-hidden="true" style={emojiStyle}>🏙️</span>
                    {t('live.table.city')}
                  </th>
                  <th style={thTd}>
                    <span role="img" aria-hidden="true" style={emojiStyle}>🖥️</span>
                    {t('live.table.ua')}
                  </th>
                  <th style={thTd}>{t('live.table.referer')}</th>
                </tr>
              </thead>
              <tbody>
                {retans.length > 0 ? retans.map((r,i) => (
                  <tr key={i} style={{ background: "#fff2e8", fontWeight: 600 }}>
                    <td style={thTd}>{r.ip_address}</td>
                    <td style={thTd}>{formatDuration(r.dur)}</td>
                    <td style={thTd}>{r.flag} {r.country}</td>
                    <td style={thTd}>{r.city}</td>
                    <td style={uaTd}>{r.user_agent}</td>
                    <td style={uaTd} title={r.referer || ''}>{r.referer || ''}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "#aaa" }}>
                      {t('live.no_relays')}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={6}>
                    <hr style={{ border: "none", borderTop: "1px solid #ccc", margin: "4px 0" }} />
                  </td>
                </tr>
                {newbies.map((r,i) => (
                  <tr key={i} style={{ background: "#e8ffe8", fontWeight: 600 }}>
                    <td style={thTd}>{r.ip_address}</td>
                    <td style={thTd}>{formatDuration(Math.floor((now - r.start)/1000))}</td>
                    <td style={thTd}>{r.flag} {r.country}</td>
                    <td style={thTd}>{r.city}</td>
                    <td style={uaTd}>{r.user_agent}</td>
                    <td style={uaTd} title={r.referer || ''}>{r.referer || ''}</td>
                  </tr>
                ))}
                {others.map((r,i) => (
                  <tr key={i}>
                    <td style={thTd}>{r.ip_address}</td>
                    <td style={thTd}>{formatDuration(r.dur)}</td>
                    <td style={thTd}>{r.flag} {r.country}</td>
                    <td style={thTd}>{r.city}</td>
                    <td style={uaTd}>{r.user_agent}</td>
                    <td style={uaTd} title={r.referer || ''}>{r.referer || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalMessage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setModalMessage(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: "min(92vw, 420px)",
              background: "#fff",
              borderRadius: 14,
              boxShadow: "0 12px 32px rgba(0,0,0,.25)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#00723a",
                color: "#fff",
                padding: "16px 22px",
                fontWeight: 700,
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{t('live.publicCounter.modalTitle')}</span>
              <button
                type="button"
                onClick={() => setModalMessage(null)}
                aria-label={t('common.close')}
                style={{
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: 28,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "20px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>
                {modalMessage}
              </div>
              <button
                type="button"
                onClick={() => setModalMessage(null)}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  background: "#00723a",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDuration(sec) {
  const h = String(Math.floor(sec/3600)).padStart(2,"0");
  const m = String(Math.floor((sec%3600)/60)).padStart(2,"0");
  const s = String(sec%60).padStart(2,"0");
  return `${h}:${m}:${s}`;
}

const widgetStyle = {
  maxWidth: 1050,
  margin: "40px auto",
  background: "#fff",
  borderRadius: 14,
  padding: 24,
  boxShadow: "0 2px 12px 0 #0001",
};
const thTd = {
  padding: "7px 12px",
  border: "1px solid #e0e0e0",
  textAlign: "left",
  verticalAlign: "top",
};
const uaTd = {
  ...thTd,
  maxWidth: 200,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};
const emojiStyle = {
  display: "inline-block",
  fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji", sans-serif',
  marginRight: 6,
};
const counterBox = {
  minWidth: 160,
  textAlign: "center",
  border: "3px solid #f33",
  borderRadius: 8,
  padding: "8px 0",
  marginLeft: 32,
};
